import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface TaxGroup {
  id: string
  name: string
  description?: string
  isActive: boolean
  items: Array<{
    id: string
    taxRate: {
      id: string
      name: string
      rate: string
      taxType: string
    }
    isCompound: boolean
    sortOrder: number
  }>
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

const TaxGroups: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [groups, setGroups] = useState<TaxGroup[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showCalculateModal, setShowCalculateModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<TaxGroup | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    items: [] as Array<{ taxRateId: string; isCompound: boolean }>,
  })
  const [calculateAmount, setCalculateAmount] = useState('')
  const [calculateResult, setCalculateResult] = useState<any>(null)

  useEffect(() => {
    fetchGroups()
    fetchTaxRates()
  }, [])

  const fetchGroups = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/tax-groups')
      setGroups(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching tax groups:', error)
      toast.error('Failed to load tax groups')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await axios.post('/api/erp/tax-groups', formData)
      toast.success('Tax group created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchGroups()
    } catch (error: any) {
      console.error('Error creating tax group:', error)
      toast.error(error.response?.data?.error || 'Failed to create tax group')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedGroup) return

    try {
      await axios.put(`/api/erp/tax-groups/${selectedGroup.id}`, formData)
      toast.success('Tax group updated successfully')
      setShowEditModal(false)
      resetForm()
      setSelectedGroup(null)
      fetchGroups()
    } catch (error: any) {
      console.error('Error updating tax group:', error)
      toast.error(error.response?.data?.error || 'Failed to update tax group')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tax group?')) return

    try {
      await axios.delete(`/api/erp/tax-groups/${id}`)
      toast.success('Tax group deleted successfully')
      fetchGroups()
    } catch (error: any) {
      console.error('Error deleting tax group:', error)
      toast.error(error.response?.data?.error || 'Failed to delete tax group')
    }
  }

  const handleCalculate = async () => {
    if (!selectedGroup || !calculateAmount) return

    try {
      const response = await axios.post(`/api/erp/tax-groups/${selectedGroup.id}/calculate`, {
        baseAmount: parseFloat(calculateAmount),
      })
      setCalculateResult(response.data.data)
    } catch (error: any) {
      console.error('Error calculating tax:', error)
      toast.error(error.response?.data?.error || 'Failed to calculate tax')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
      items: [],
    })
  }

  const openCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const openEditModal = (group: TaxGroup) => {
    setSelectedGroup(group)
    setFormData({
      name: group.name,
      description: group.description || '',
      isActive: group.isActive,
      items: group.items.map(item => ({
        taxRateId: item.taxRate.id,
        isCompound: item.isCompound,
      })),
    })
    setShowEditModal(true)
  }

  const openViewModal = (group: TaxGroup) => {
    setSelectedGroup(group)
    setShowViewModal(true)
  }

  const openCalculateModal = (group: TaxGroup) => {
    setSelectedGroup(group)
    setCalculateAmount('')
    setCalculateResult(null)
    setShowCalculateModal(true)
  }

  const addTaxRate = () => {
    if (taxRates.length === 0) {
      toast.error('No tax rates available')
      return
    }
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { taxRateId: taxRates[0].id, isCompound: false },
      ],
    })
  }

  const removeTaxRate = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    })
  }

  const updateTaxRate = (index: number, field: 'taxRateId' | 'isCompound', value: string | boolean) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setFormData({ ...formData, items: newItems })
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_tax_rate', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view tax groups.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Groups</h1>
          <p className="text-gray-600 mt-1">Manage tax groups with compound tax support</p>
        </div>
        {hasPermission('erp_tax_rate', 'write') && (
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Create Tax Group
          </button>
        )}
      </div>

      <PageHelp articleTitle="Tax Groups" />

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading tax groups...</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-600">No tax groups found. Create your first tax group to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tax Rates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {group.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {group.description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {group.items.length} rate{group.items.length !== 1 ? 's' : ''}
                    {group.items.some(item => item.isCompound) && (
                      <span className="ml-2 text-xs text-blue-600">(with compound)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        group.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {group.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openViewModal(group)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    <button
                      onClick={() => openCalculateModal(group)}
                      className="text-green-600 hover:text-green-900 mr-4"
                    >
                      Calculate
                    </button>
                    {hasPermission('erp_tax_rate', 'write') && (
                      <>
                        <button
                          onClick={() => openEditModal(group)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_tax_rate', 'delete') && (
                          <button
                            onClick={() => handleDelete(group.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Tax Group</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rates *
                  </label>
                  <button
                    type="button"
                    onClick={addTaxRate}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Tax Rate
                  </button>
                </div>
                {formData.items.length === 0 ? (
                  <p className="text-sm text-gray-500">No tax rates added. Click "Add Tax Rate" to add one.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <select
                          value={item.taxRateId}
                          onChange={(e) => updateTaxRate(index, 'taxRateId', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Select tax rate</option>
                          {taxRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.name} ({rate.rate}%)
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={item.isCompound}
                            onChange={(e) => updateTaxRate(index, 'isCompound', e.target.checked)}
                            className="mr-1"
                          />
                          <span className="text-gray-700">Compound</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => removeTaxRate(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Tax Group</h2>
            <form onSubmit={handleUpdate}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Rates *
                  </label>
                  <button
                    type="button"
                    onClick={addTaxRate}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Tax Rate
                  </button>
                </div>
                {formData.items.length === 0 ? (
                  <p className="text-sm text-gray-500">No tax rates added. Click "Add Tax Rate" to add one.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <select
                          value={item.taxRateId}
                          onChange={(e) => updateTaxRate(index, 'taxRateId', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          required
                        >
                          <option value="">Select tax rate</option>
                          {taxRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.name} ({rate.rate}%)
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={item.isCompound}
                            onChange={(e) => updateTaxRate(index, 'isCompound', e.target.checked)}
                            className="mr-1"
                          />
                          <span className="text-gray-700">Compound</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => removeTaxRate(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    resetForm()
                    setSelectedGroup(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Tax Group Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedGroup.name}</p>
              </div>
              {selectedGroup.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedGroup.description}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      selectedGroup.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedGroup.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rates</label>
                <div className="space-y-2">
                  {selectedGroup.items.map((item, index) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">
                            {index + 1}. {item.taxRate.name} ({item.taxRate.rate}%)
                          </p>
                          {item.isCompound && (
                            <p className="text-xs text-blue-600 mt-1">Compound tax (calculated on base + previous taxes)</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedGroup(null)
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Modal */}
      {showCalculateModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Calculate Tax</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={calculateAmount}
                  onChange={(e) => setCalculateAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="0.00"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowCalculateModal(false)
                    setSelectedGroup(null)
                    setCalculateAmount('')
                    setCalculateResult(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCalculate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Calculate
                </button>
              </div>
              {calculateResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded">
                  <p className="text-sm font-medium text-gray-700 mb-2">Total Tax: £{calculateResult.totalTax.toFixed(2)}</p>
                  <div className="space-y-1">
                    {calculateResult.breakdown.map((item: any, index: number) => (
                      <p key={index} className="text-xs text-gray-600">
                        {item.taxRate}: £{item.amount.toFixed(2)}
                        {item.isCompound && <span className="text-blue-600"> (compound)</span>}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaxGroups
